import type { Team } from '@compass/client/types/team';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronsUpDown, Loader2 } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { AssigneeMemberDisplay } from '@/components/AssigneeMemberDisplay';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MultiSelect } from '@/components/ui/multi-select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useOrganizationUsers } from '@/hooks/useOrganizationUsers';
import { useOrgProgramWorkflows } from '@/hooks/useOrgProgramWorkflows';
import {
  useLinkTeamToProgram,
  useOrganizationTeams,
  useUnlinkTeamFromProgram,
  useUpdateOrganizationTeam,
} from '@/hooks/useTeams';
import { useUsersSkillsMap } from '@/hooks/useUserSkills';
import { workflowService } from '@/services/api';
import { useStore } from '@/store';
import { getApiErrorMessage } from '@/utils/apiError';
import { hasLeaderSkill } from '@/utils/skills';

import { ALREADY_ON_TEAM_HINT, usersAlreadyOnATeam } from './teamMembership';

interface EditTeamDialogProps {
  organizationId: string;
  /**
   * When provided, the dialog operates in program-scoped mode: it shows a workflow-type
   * selector with workflows from this single program (used from ProgramTeams). When omitted
   * (org settings), it shows the workflow-type-driven multi-program association UI.
   */
  programId?: string;
  team: Team | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const EditTeamDialog: React.FC<EditTeamDialogProps> = ({
  organizationId,
  programId,
  team,
  open,
  onOpenChange,
}) => {
  const isProgramScoped = !!programId;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [managerId, setManagerId] = useState('');
  const [selectedWorkflowTypes, setSelectedWorkflowTypes] = useState<string[]>([]);
  // Org-settings (multi-program) mode state
  const [workflowType, setWorkflowType] = useState('');
  const [selectedProgramIds, setSelectedProgramIds] = useState<string[]>([]);
  const [managerComboboxOpen, setManagerComboboxOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const queryClient = useQueryClient();
  const { data: orgUsersData } = useOrganizationUsers(organizationId, { limit: 100 });
  const updateMutation = useUpdateOrganizationTeam();
  const linkTeamMutation = useLinkTeamToProgram();
  const unlinkTeamMutation = useUnlinkTeamFromProgram();
  const { teams: orgTeams } = useOrganizationTeams(organizationId);

  const orgUsers = orgUsersData?.users || [];
  const selectedManager = orgUsers.find(u => u.user_id === managerId);

  const assignedElsewhere = useMemo(
    () => usersAlreadyOnATeam(orgTeams, team?.id),
    [orgTeams, team?.id],
  );

  // ─── Skill leader ⇒ team lead (BI-1698) ─────────────────────────────────────
  // Only a team lead may hold skill leadership, so replacing the lead is blocked while the
  // OUTGOING lead still leads a skill in this org — they must be demoted in Manage Skills
  // first. There is no constraint on the incoming lead: a team lead need not be a skill
  // leader at all (the BI-1544 rule in that direction was removed). We therefore only need
  // the current lead's skills, not every candidate's.
  const currentLeadId = team?.manager_id || '';
  const candidateUserIds = useMemo(
    () => (open && currentLeadId ? [currentLeadId] : []),
    [open, currentLeadId],
  );
  const { skillsMap, isLoading: skillsLoading } = useUsersSkillsMap(
    organizationId,
    candidateUserIds,
  );

  const managerChanged = managerId !== currentLeadId;
  // Only meaningful once skills have loaded; while loading we don't block on incomplete
  // data (the backend still guards authoritatively).
  const outgoingLeadStillSkillLeader =
    managerChanged &&
    !!currentLeadId &&
    !skillsLoading &&
    hasLeaderSkill(skillsMap.get(currentLeadId));

  // ─── Program-scoped mode: workflows for the single program ──────────────────
  const { data: workflowsData } = useQuery({
    queryKey: ['workflows', 'byProgram', programId],
    queryFn: () =>
      workflowService.getWorkflowsByProgram({
        organization_id: organizationId,
        program_id: programId!,
      }),
    enabled: isProgramScoped && open,
    staleTime: 5 * 60 * 1000,
  });
  const workflows = useMemo(
    () => (workflowsData?.workflows || []).filter(w => w.id),
    [workflowsData?.workflows],
  );

  // ─── Org-settings mode: org-wide workflow types and their programs ──────────
  const { workflowTypeOptions, programsByWorkflowType } = useOrgProgramWorkflows(
    open && !isProgramScoped,
  );
  const storePrograms = useStore(state => state.programs);

  // Programs the team is currently linked to (from program.teams[]).
  const linkedProgramIds = useMemo(() => {
    if (!team?.id) return [] as string[];
    return Object.values(storePrograms)
      .filter(p => p.id && p.teams?.includes(team.id!))
      .map(p => p.id!);
  }, [storePrograms, team?.id]);

  // Programs that contain the currently selected workflow type.
  const filteredProgramIds = useMemo(() => {
    if (!workflowType) return [] as string[];
    return (programsByWorkflowType.get(workflowType) || [])
      .map(p => p.id)
      .filter((id): id is string => !!id);
  }, [programsByWorkflowType, workflowType]);

  const programOptions = useMemo(
    () =>
      (programsByWorkflowType.get(workflowType) || [])
        .filter(p => p.id)
        .map(p => ({ value: p.id!, label: p.name || p.id! })),
    [programsByWorkflowType, workflowType],
  );

  // Seed form fields when the dialog opens.
  useEffect(() => {
    if (open && team) {
      setName(team.name || '');
      setDescription(team.description || '');
      setManagerId(team.manager_id || '');
      setSelectedWorkflowTypes(team.workflow_types || []);
      setWorkflowType(team.workflow_types?.[0] || '');
    }
    if (!open) {
      setManagerComboboxOpen(false);
      setIsSaving(false);
    }
  }, [open, team]);

  // Org mode: (re)seed the program selection to the linked programs that fall within the
  // current workflow-type filter whenever the selected type (or its program set) changes.
  const filteredKey = filteredProgramIds.join(',');
  useEffect(() => {
    if (!open || isProgramScoped) return;
    const baseline = filteredProgramIds.filter(id => linkedProgramIds.includes(id));
    setSelectedProgramIds(baseline);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isProgramScoped, workflowType, filteredKey, linkedProgramIds.join(',')]);

  const handleSubmit = async () => {
    if (!team?.id || !name.trim()) return;

    setIsSaving(true);
    try {
      await updateMutation.mutateAsync({
        organization_id: organizationId,
        team_id: team.id,
        name: name.trim(),
        description: description.trim(),
        manager_id: managerId || undefined,
      });

      if (isProgramScoped) {
        // Diff workflow types and apply changes in a single call
        const currentTypes = new Set(team.workflow_types || []);
        const newTypes = new Set(selectedWorkflowTypes);
        const toAdd = selectedWorkflowTypes.filter(t => !currentTypes.has(t));
        const toRemove = (team.workflow_types || []).filter(t => !newTypes.has(t));
        if (toAdd.length > 0 || toRemove.length > 0) {
          // Use the org-scoped endpoint so org-admins (who hold team:write:organization,
          // not team:write:global) are authorized. The global /teams/{id} route is
          // system-admin only and would 403 here.
          await updateMutation.mutateAsync({
            organization_id: organizationId,
            team_id: team.id,
            add_workflow_types: toAdd.length > 0 ? toAdd : undefined,
            remove_workflow_types: toRemove.length > 0 ? toRemove : undefined,
          });
          await queryClient.invalidateQueries({ queryKey: ['compass', 'teams'] });
        }
      } else if (workflowType) {
        // Ensure the team carries the selected workflow type.
        if (!(team.workflow_types || []).includes(workflowType)) {
          // Org-scoped endpoint (see note above) — global /teams/{id} would 403 for org-admins.
          await updateMutation.mutateAsync({
            organization_id: organizationId,
            team_id: team.id,
            add_workflow_types: [workflowType],
          });
          await queryClient.invalidateQueries({ queryKey: ['compass', 'teams'] });
        }

        // Full sync of program links, scoped to the filtered (workflow-type) set: link
        // newly selected programs, unlink those that were linked-in-filter but deselected.
        // Programs linked under a different workflow type are left untouched.
        const linkedInFilter = filteredProgramIds.filter(id => linkedProgramIds.includes(id));
        const toLink = selectedProgramIds.filter(id => !linkedProgramIds.includes(id));
        const toUnlink = linkedInFilter.filter(id => !selectedProgramIds.includes(id));

        await Promise.all([
          ...toLink.map(pid =>
            linkTeamMutation.mutateAsync({
              organizationId,
              programId: pid,
              teamId: team.id!,
            }),
          ),
          ...toUnlink.map(pid =>
            unlinkTeamMutation.mutateAsync({
              organizationId,
              programId: pid,
              teamId: team.id!,
            }),
          ),
        ]);
      }

      toast.success(`Team "${name.trim()}" updated`);
      onOpenChange(false);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to update team'));
    } finally {
      setIsSaving(false);
    }
  };

  if (!team) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit Team</DialogTitle>
          <DialogDescription>
            {isProgramScoped
              ? 'Update team name, description, or team lead.'
              : 'Update team details and the programs this team is assigned to.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="edit-team-name">Name</Label>
            <Input
              id="edit-team-name"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Enter team name"
              className="mt-2"
            />
          </div>

          <div>
            <Label htmlFor="edit-team-description">Description</Label>
            <Input
              id="edit-team-description"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Optional description"
              className="mt-2"
            />
          </div>

          <div>
            <Label>Team Lead</Label>
            <Popover open={managerComboboxOpen} onOpenChange={setManagerComboboxOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={managerComboboxOpen}
                  className="mt-2 w-full justify-between"
                >
                  {selectedManager
                    ? selectedManager.name || selectedManager.email
                    : 'Select a team lead...'}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[450px] p-0">
                <Command>
                  <CommandInput placeholder="Search users..." />
                  <CommandEmpty>No users found.</CommandEmpty>
                  <CommandList
                    className="max-h-[200px] overflow-y-auto"
                    onWheel={e => e.stopPropagation()}
                  >
                    <CommandGroup>
                      {orgUsers.map(user => {
                        const uid = user.user_id || '';
                        const isDisabled = assignedElsewhere.has(uid);
                        const hint = isDisabled ? ALREADY_ON_TEAM_HINT : null;
                        return (
                          <CommandItem
                            key={user.user_id}
                            value={`${user.name} ${user.email}`}
                            disabled={isDisabled}
                            onSelect={() => {
                              if (isDisabled) return;
                              setManagerId(user.user_id || '');
                              setManagerComboboxOpen(false);
                            }}
                          >
                            <AssigneeMemberDisplay
                              name={user.name || undefined}
                              email={user.email || ''}
                              userId={user.user_id}
                            />
                            {hint && (
                              <span className="ml-auto text-xs text-muted-foreground">{hint}</span>
                            )}
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {outgoingLeadStillSkillLeader && (
              <p className="mt-2 text-sm text-destructive">
                The current team lead is still a skill leader. Only a team lead can lead a skill —
                remove their skill leadership in Manage Skills before handing the team over.
              </p>
            )}
          </div>

          {/* Program-scoped mode: workflow type selector for the single program */}
          {isProgramScoped && workflows.length > 0 && (
            <div>
              <Label>Workflow Types</Label>
              <MultiSelect
                className="mt-2"
                options={workflows.map(w => ({
                  label: w.name || w.workflow_type || w.id || '',
                  value: w.workflow_type || w.id || '',
                }))}
                selected={selectedWorkflowTypes}
                onChange={setSelectedWorkflowTypes}
                placeholder="Select workflow types"
                searchable
              />
            </div>
          )}

          {/* Org-settings mode: workflow type → filtered multi-program association */}
          {!isProgramScoped && (
            <>
              <div>
                <Label>Workflow Type</Label>
                <Select value={workflowType} onValueChange={setWorkflowType}>
                  <SelectTrigger className="mt-2 text-left">
                    <SelectValue placeholder="Select a workflow type" />
                  </SelectTrigger>
                  <SelectContent>
                    {workflowTypeOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Programs</Label>
                <MultiSelect
                  className="mt-2"
                  options={programOptions}
                  selected={selectedProgramIds}
                  onChange={setSelectedProgramIds}
                  placeholder={workflowType ? 'Select programs' : 'Select a workflow type first'}
                  disabled={!workflowType}
                  searchable
                  maxCount={2}
                  countNoun="program"
                />
                {workflowType && programOptions.length === 0 && (
                  <p className="mt-2 text-sm text-muted-foreground">
                    No programs have this workflow type.
                  </p>
                )}
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!name.trim() || isSaving || outgoingLeadStillSkillLeader}
          >
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
